<!-- Desktop Header -->
<div class="navigation-template__header-desktop">
	<div class="navigation-template__header-desktop-left">
		<button
			tuiIconButton
			appearance="ghost"
			size="m"
			(click)="onToggleSidebar()"
		>
			@if (sidebarCollapsed()) {
				<tui-icon size="m" icon="@tui.menu" />
			} @else {
				<tui-icon size="m" icon="@tui.arrow-left-to-line" />
			}
		</button>

		<img
			draggable="false"
			class="navigation-template__header-desktop-wordmark"
			src="assets/images/wordmark.png"
		/>
	</div>

	<ctx-global-search-bar />

	<div class="navigation-template__header-desktop-right">
		<button
			tuiButton
			appearance="ghost"
			size="m"
			[tuiDropdown]="accountDropdown"
			[(tuiDropdownOpen)]="accountMenuOpen"
		>
			@if (authService.authUser?.role !== 'super') {
				<ctx-avatar [user]="authService.user ?? authService.authUser" />
			} @else {
				<ctx-avatar [user]="authService.authUser" />
			}

			<div
				class="navigation-template__header-desktop-right-name text-left"
			>
				<p class="font-bold">
					{{ authService.user ?? authService.authUser | userName }}
				</p>

				<p class="font-size-xsmall color-tertiary">
					{{ authService.organization()!.name }}
				</p>
			</div>

			<tui-icon icon="@tui.chevron-down" />
		</button>

		<ng-template #accountDropdown>
			<ctx-detail-menu-dropdown [menuItems]="accountMenuItems()">
				<div
					header
					class="bg-primary-200 py-4 px-6 mt-[-4px] mx-[-6px] text-left flex flex-col items-center gap-2"
				>
					<ctx-avatar
						[user]="authService.user ?? authService.authUser"
					/>

					<div class="flex flex-col items-center">
						<p class="font-bold text-black text-sm">
							{{ authService.user | userName }}
						</p>
						<p class="text-xs text-black opacity-50">
							{{ authService.user.email }}
						</p>
					</div>
				</div>

				<div class="text-right border-t border-gray-300 p-2">
					<p class="font-size-xsmall color-tertiary opacity-50">
						{{ 'app.version' | transloco: { version: version } }}
					</p>
				</div>
			</ctx-detail-menu-dropdown>
		</ng-template>
	</div>
</div>

<!-- Mobile Header -->
<div class="navigation-template__header-mobile">
	<button
		tuiIconButton
		class="secondary"
		appearance="ghost"
		size="m"
		(click)="onMenuOpen()"
	>
		<tui-icon size="l" icon="@tui.menu" />
	</button>

	<button
		tuiIconButton
		class="secondary"
		appearance="ghost"
		size="m"
		(click)="onSearchOpen()"
	>
		<tui-icon size="l" icon="@tui.search" />
	</button>
</div>

<!-- Desktop and Mobile Main Stage -->
<div class="navigation-template__main">
	<!-- Desktop Sidebar -->
	<div class="navigation-template__main-sidebar">
		<div class="navigation-template__main-sidebar-top">
			@if (sidebarCollapsed()) {
				<button
					tuiIconButton
					iconStart="@tui.plus"
					size="l"
					appearance="primary"
					(click)="onPromptCreate()"
				></button>
			} @else {
				<button
					tuiButton
					iconStart="@tui.plus"
					size="l"
					appearance="primary"
					(click)="onPromptCreate()"
				>
					{{ 'action.create-new' | transloco }}
				</button>
			}
		</div>

		<div class="navigation-template__main-sidebar-navigation">
			@for (item of filteredMenuItems; track item.key) {
				@if (
					(item.location === 'side' || item.location === 'all') &&
					(!item.requiredRole || (item.requiredRole | roleAccess))
				) {
					@if (item.badge$ | async; as count) {
						<tui-badged-content>
							<tui-badge-notification tuiSlot="top">
								{{ count }}
							</tui-badge-notification>

							<ng-container
								*ngTemplateOutlet="
									sidebarButtonContent;
									context: { item }
								"
							/>
						</tui-badged-content>
					} @else {
						<ng-container
							*ngTemplateOutlet="
								sidebarButtonContent;
								context: { item }
							"
						/>
					}
				}
			}
		</div>

		@if (authService.authUser?.role !== 'super') {
			<div class="navigation-template__main-sidebar-bottom">
				<ng-container
					*ngTemplateOutlet="
						sidebarButtonContent;
						context: { item: helpItem }
					"
				/>
			</div>
		}
	</div>

	<div class="navigation-template__main-route-container">
		<router-outlet />
	</div>
</div>

<!-- Mobile Tab Bar -->
<nav class="navigation-template__tab-bar" tuiTabBar>
	@for (item of filteredMenuItems; track item.key) {
		@if (
			(item.location === 'tab' || item.location === 'all') &&
			(!item.requiredRole || (item.requiredRole | roleAccess))
		) {
			<tui-badged-content>
				@if (item.badge$ | async; as count) {
					<tui-badge-notification tuiSlot="top">
						{{ count }}
					</tui-badge-notification>
				}

				@if (item.handler) {
					<a
						class="cursor-pointer"
						[icon]="item.icon"
						tuiTabBarItem
						(click)="item.handler()"
					>
						{{ 'navigation.' + item.key | transloco }}
					</a>
				} @else if (item.route) {
					<a
						class="cursor-pointer"
						[icon]="item.icon"
						routerLinkActive="active"
						tuiTabBarItem
						[routerLink]="item.route"
					>
						{{ 'navigation.' + item.key | transloco }}
					</a>
				}
			</tui-badged-content>
		}
	}
</nav>

<ng-template #createTemplate>
	<section tuiContent>
		@for (entity of entityTypes; track entity.key) {
			@if (!entity.requiredRole || (entity.requiredRole | roleAccess)) {
				<button
					tuiButton
					class="menu-button"
					[iconStart]="entity.icon"
					iconEnd="@tui.chevron-right"
					(click)="onCreate(entity.key)"
				>
					<span>{{ 'data.' + entity.key | transloco }}</span>
				</button>
			}
		}
	</section>
</ng-template>

<ng-template #sidebarButtonContent let-item="item">
	@if (item.handler) {
		<a tuiLink size="l" class="nav-button" (click)="item.handler()">
			<tui-icon [icon]="item.icon" />
			@if (!sidebarCollapsed()) {
				{{ 'navigation.' + item.key | transloco }}
			}
		</a>
	} @else {
		<a
			tuiLink
			size="l"
			class="nav-button"
			[routerLink]="[item.route]"
			routerLinkActive="active"
		>
			<tui-icon [icon]="item.icon" />
			@if (!sidebarCollapsed()) {
				{{ 'navigation.' + item.key | transloco }}
			}
		</a>
	}
</ng-template>
